import type { KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { FilterDateField } from './FilterDateField';
import { FilterMultiSelectField } from './FilterMultiSelectField';
import { FilterSelectField } from './FilterSelectField';
import { FILTER_CONTROL_CLASS } from './advancedFilter.constants';
import type { FilterField } from './advancedFilter.types';
import { getDateLimits } from './dateLimits';

const FIELD_LABEL_CLASS =
  'pointer-events-none absolute top-1/2 left-2 block max-w-[calc(100%-1rem)] origin-left -translate-y-1/2 truncate bg-card px-1 text-sm font-normal text-muted-foreground transition-[top,scale,color] duration-200 ease-out';
const FLOATED_FIELD_LABEL_CLASS =
  'group-data-filled/filter-field:top-0 group-data-filled/filter-field:scale-80 group-focus-within/filter-field:top-0 group-focus-within/filter-field:scale-80 group-has-[[aria-expanded=true]]/filter-field:top-0 group-has-[[aria-expanded=true]]/filter-field:scale-80';
const ACTIVE_FIELD_LABEL_CLASS =
  'group-data-filled/filter-field:font-medium group-data-filled/filter-field:text-primary group-focus-within/filter-field:text-primary group-has-[[aria-expanded=true]]/filter-field:text-primary';
const INPUT_PLACEHOLDER_CLASS = 'placeholder:opacity-0 focus:placeholder:opacity-100';
const FIELD_BOX_CLASS = 'group/filter-field relative';
const DISABLED_FIELD_HINT_CLASS =
  'cursor-not-allowed rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/50 [&_button:disabled]:pointer-events-none';
const SUBMIT_KEY = 'Enter';

interface FilterFieldItemProps {
  field: FilterField;
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  onSubmit?: (() => void) | undefined;
}

export function FilterFieldItem({ field, values, onChange, onSubmit }: FilterFieldItemProps) {
  const value = values[field.key] ?? '';

  function handleChange(nextValue: string) {
    onChange({ ...values, [field.key]: nextValue });
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === SUBMIT_KEY) onSubmit?.();
  }

  function renderControl() {
    if (field.type === 'select') {
      return <FilterSelectField field={field} value={value} onChange={handleChange} />;
    }
    if (field.type === 'multiSelect') {
      return <FilterMultiSelectField field={field} value={value} onChange={handleChange} />;
    }
    if (field.type === 'date') {
      const { minDate, maxDate } = getDateLimits(field, values);
      return (
        <FilterDateField
          field={field}
          value={value}
          onChange={handleChange}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
    }
    return (
      <Input
        type={field.type}
        value={value}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={handleInputKeyDown}
        placeholder={field.placeholder ?? field.label}
        className={cn(FILTER_CONTROL_CLASS, INPUT_PLACEHOLDER_CLASS)}
      />
    );
  }

  const fieldContent = (
    <>
      {renderControl()}
      <Label className={cn(FIELD_LABEL_CLASS, FLOATED_FIELD_LABEL_CLASS, ACTIVE_FIELD_LABEL_CLASS)}>
        {field.label}
        {field.isRequired ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
    </>
  );
  const filledState = value ? '' : undefined;

  if (!field.disabled || !field.disabledHint) {
    return (
      <div data-filled={filledState} className={FIELD_BOX_CLASS}>
        {fieldContent}
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={<div data-filled={filledState} />}
        tabIndex={0}
        aria-label={`${field.label}: ${field.disabledHint}`}
        className={cn(FIELD_BOX_CLASS, DISABLED_FIELD_HINT_CLASS)}
      >
        {fieldContent}
      </TooltipTrigger>
      <TooltipContent>{field.disabledHint}</TooltipContent>
    </Tooltip>
  );
}
